import React, { useState, useEffect } from "react";
import { saveLead } from "./supabase";

const luxuryGradient = "bg-gradient-to-br from-[#0B1C2C] via-[#0F2A44] to-[#123B63]";
const goldGradient = "bg-gradient-to-r from-[#C9A96A] to-[#E5C07B]";

const languageOptions = [
  { code: "en", label: "EN", flag: "🇬🇧" },
  { code: "fr", label: "FR", flag: "🇫🇷" },
  { code: "es", label: "ES", flag: "🇪🇸" },
  { code: "it", label: "IT", flag: "🇮🇹" },
  { code: "de", label: "DE", flag: "🇩🇪" },
  { code: "ar", label: "AR", flag: "🇸🇦" },
  { code: "ru", label: "RU", flag: "🇷🇺" },
];

const flagStyles = {
  en: { background: "linear-gradient(135deg, #012169 0 42%, #fff 42% 48%, #c8102e 48% 54%, #fff 54% 60%, #012169 60% 100%)" },
  fr: { background: "linear-gradient(to right, #0055a4 0 33%, #fff 33% 66%, #ef4135 66% 100%)" },
  es: { background: "linear-gradient(to bottom, #aa151b 0 25%, #f1bf00 25% 75%, #aa151b 75% 100%)" },
  it: { background: "linear-gradient(to right, #009246 0 33%, #fff 33% 66%, #ce2b37 66% 100%)" },
  de: { background: "linear-gradient(to bottom, #000 0 33%, #dd0000 33% 66%, #ffce00 66% 100%)" },
  ar: { background: "linear-gradient(to bottom, #006c35 0 100%)" },
  ru: { background: "linear-gradient(to bottom, #fff 0 33%, #0039a6 33% 66%, #d52b1e 66% 100%)" },
};

function FlagMark({ code }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3.5 w-5 shrink-0 rounded-[3px] ring-1 ring-white/70"
      style={flagStyles[code] || flagStyles.en}
    />
  );
}

const translations = {
  ru: {
    backToSite: "← Назад на сайт",
    partnerNetwork: "Партнерская сеть",
    whatIsRCI: "Что такое RCI?",
    heroText: "RCI — ведущая мировая сеть обмена отдыхом для владельцев vacation ownership и курортной недвижимости. Основанная в 1974 году, она позволяет участникам обменивать свои отпускные недели и путешествовать по тысячам курортов по всему миру.",
    visitRCI: "Посетить RCI.com →",
    affiliatedResorts: "Аффилированные курорты",
    countries: "Страны",
    memberFamilies: "Семьи участников",
    bookingsWorldwide: "Бронирования по всему миру",
    howItWorks: "Как это работает",
    fourSteps: "4 шага для обмена отдыха",
    steps: [
      { title: "Участник владеет неделей отдыха", text: "Участник владеет одной неделей или апартаментом на курорте, аффилированном с RCI, например апартаментом Sunny Vacation Club в Caribbean World Mahdia." },
      { title: "Депонирование недели", text: "Участник вносит свои отпускные недели в систему RCI, делая их доступными для обмена." },
      { title: "Получение обменной силы", text: "RCI присваивает значение Trading Power на основе сезона, качества курорта, размера объекта, спроса и срока депонирования." },
      { title: "Обмен на другой отдых", text: "Участники используют Trading Power для бронирования отдыха на других курортах по всему миру." },
    ],
    additionalBenefits: "Дополнительные преимущества",
    moreThan: "Больше, чем просто обмен",
    benefits: [
      { title: "Обмен отдыхом", items: ["Обменяйте свою неделю на другое направление по всему миру", "Доступ к 3 600+ курортам по всему миру", "Фиксированная плата за обмен €275 за неделю"] },
      { title: "Дополнительный отдых", items: ["Бронируйте дополнительные отпуска за оплату", "Не нужно использовать свою неделю", "Удобно для расширения календаря поездок"] },
      { title: "Скидки на отели", items: ["Используйте Trading Power для скидок в 600 000+ отелях по всему миру", "Включает крупные сети и бутик-отели", "Доступно напрямую через портал RCI"] },
    ],
    forVacationClubs: "Для vacation club",
    whyValuable: "Почему RCI ценен",
    whyText: "RCI подходит для vacation club, курортных резиденций и форматов отпускной собственности, потому что дает владельцам гибкость размещения по всему миру вместо привязки к одному месту.",
    whyPoints: [
      "Дает участникам гибкость международных путешествий",
      "Повышает ценность курортной собственности",
      "Делает проекты vacation club более привлекательными",
      "Подключает курорты к глобальной туристической сети",
    ],
    ctaTitle: "Интересует Sunny Vacation Club?",
    ctaText: "Участники Caribbean World Mahdia получают 3-летнее членство RCI, включенное в пакет владения.",
    ctaButton: "Связаться с отделом продаж",
    disclaimer: "Информация RCI предоставлена для справки. Возможности обмена зависят от права членства, доступности, правил курорта, сезона и применимых сборов.",
  },
  en: {
    backToSite: "← Back to site",
    partnerNetwork: "Partner Network",
    whatIsRCI: "What is RCI?",
    heroText: "RCI is the world's leading vacation exchange network for timeshare and holiday property owners. Founded in 1974, it allows members to exchange their holiday weeks and travel to thousands of resorts worldwide.",
    visitRCI: "Visit RCI.com →",
    affiliatedResorts: "Affiliated resorts",
    countries: "Countries",
    memberFamilies: "Member families",
    bookingsWorldwide: "Bookings worldwide",
    howItWorks: "How it works",
    fourSteps: "4 steps to exchange your holiday",
    steps: [
      { title: "Member Owns a Holiday Week", text: "A member owns one week or an apartment in a RCI affiliated resort — such as a Sunny Vacation Club apartment at Caribbean World Mahdia." },
      { title: "Deposit the Week", text: "The member deposits his vacation weeks into the RCI system, making them available for exchange." },
      { title: "Receive Trading Power", text: "RCI assigns a value called Trading Power based on season, resort quality, unit size, demand, and deposit timing." },
      { title: "Exchange for Another Vacation", text: "Members use their Trading Power to book vacations at other resorts around the world." },
    ],
    additionalBenefits: "Additional benefits",
    moreThan: "More than just an exchange",
    benefits: [
      { title: "Exchange Holidays", items: ["Exchange your week for another destination worldwide", "Access 3,600+ resorts globally", "€275 flat exchange fee per week"] },
      { title: "Extra Holidays", items: ["Book additional vacations with cash", "No need to use your owned week", "Great for topping up your travel calendar"] },
      { title: "Hotel Discounts", items: ["Use Trading Power for discounts on 600,000+ hotels worldwide", "Covers major chains and boutique properties", "Available directly through the RCI portal"] },
    ],
    forVacationClubs: "For vacation clubs",
    whyValuable: "Why RCI is valuable",
    whyText: "RCI is ideal for projects like vacation clubs, resort residences, and timeshare-style properties because it allows owners to enjoy flexible worldwide accommodations instead of staying in only one location.",
    whyPoints: [
      "Gives international travel flexibility to members",
      "Adds value to resort ownership",
      "Increases attractiveness of vacation club projects",
      "Connects resorts to a global travel network",
    ],
    ctaTitle: "Interested in Sunny Vacation Club?",
    ctaText: "Caribbean World Mahdia members receive a 3-year RCI membership included with their ownership package.",
    ctaButton: "Contact the Sales Team",
    disclaimer: "RCI information provided for reference only. Exchange opportunities are subject to membership eligibility, availability, resort rules, season, and applicable fees.",
  },
  fr: {
    backToSite: "← Retour au site",
    partnerNetwork: "Réseau partenaire",
    whatIsRCI: "Qu'est-ce que RCI ?",
    heroText: "RCI est le premier réseau mondial d'échange de vacances pour les propriétaires de résidences en multipropriété. Fondé en 1974, il permet aux membres d'échanger leurs semaines de vacances et de voyager dans des milliers de resorts à travers le monde.",
    visitRCI: "Visiter RCI.com →",
    affiliatedResorts: "Resorts affiliés",
    countries: "Pays",
    memberFamilies: "Familles membres",
    bookingsWorldwide: "Réservations dans le monde",
    howItWorks: "Comment ça marche",
    fourSteps: "4 étapes pour échanger vos vacances",
    steps: [
      { title: "Le membre possède une semaine de vacances", text: "Un membre possède une semaine ou un appartement dans un resort affilié RCI — comme un appartement Sunny Vacation Club à Caribbean World Mahdia." },
      { title: "Déposer la semaine", text: "Le membre dépose ses semaines de vacances dans le système RCI, les rendant disponibles pour l'échange." },
      { title: "Recevoir le Trading Power", text: "RCI attribue une valeur appelée Trading Power en fonction de la saison, de la qualité du resort, de la taille de l'unité, de la demande et du timing du dépôt." },
      { title: "Échanger pour une autre destination", text: "Les membres utilisent leur Trading Power pour réserver des vacances dans d'autres resorts à travers le monde." },
    ],
    additionalBenefits: "Avantages supplémentaires",
    moreThan: "Plus qu'un simple échange",
    benefits: [
      { title: "Échanges de vacances", items: ["Échangez votre semaine contre une autre destination dans le monde", "Accédez à plus de 3 600 resorts dans le monde", "Frais d'échange forfaitaires : 275 € / semaine"] },
      { title: "Vacances supplémentaires", items: ["Réservez des vacances supplémentaires en espèces", "Sans utiliser votre semaine propriétaire", "Idéal pour compléter votre calendrier de voyages"] },
      { title: "Réductions hôtelières", items: ["Utilisez votre Trading Power pour des réductions dans plus de 600 000 hôtels", "Couvre les grandes chaînes et les hôtels boutique", "Disponible directement via le portail RCI"] },
    ],
    forVacationClubs: "Pour les vacation clubs",
    whyValuable: "Pourquoi RCI est précieux",
    whyText: "RCI est idéal pour les projets de vacation clubs, résidences resort et propriétés en multipropriété car il permet aux propriétaires de voyager librement dans le monde entier au lieu de rester dans un seul endroit.",
    whyPoints: [
      "Offre une flexibilité de voyage internationale aux membres",
      "Ajoute de la valeur à la propriété resort",
      "Augmente l'attractivité des projets de vacation club",
      "Connecte les resorts à un réseau mondial de voyages",
    ],
    ctaTitle: "Intéressé par Sunny Vacation Club ?",
    ctaText: "Les membres de Caribbean World Mahdia reçoivent une adhésion RCI de 3 ans incluse dans leur package de propriété.",
    ctaButton: "Contacter l'équipe commerciale",
    disclaimer: "Informations RCI fournies à titre indicatif uniquement. Les opportunités d'échange dépendent de l'éligibilité, des disponibilités, des règles du resort, de la saison et des frais applicables.",
  },
  it: {
    backToSite: "← Torna al sito",
    partnerNetwork: "Rete partner",
    whatIsRCI: "Cos'è RCI?",
    heroText: "RCI è il principale network mondiale di scambio vacanze per proprietari di immobili in multiproprietà. Fondato nel 1974, permette ai membri di scambiare le proprie settimane vacanza e viaggiare in migliaia di resort nel mondo.",
    visitRCI: "Visita RCI.com →",
    affiliatedResorts: "Resort affiliati",
    countries: "Paesi",
    memberFamilies: "Famiglie member",
    bookingsWorldwide: "Prenotazioni mondiali",
    howItWorks: "Come funziona",
    fourSteps: "4 passi per scambiare la tua vacanza",
    steps: [
      { title: "Il membro possiede una settimana vacanza", text: "Un membro possiede una settimana o un appartamento in un resort affiliato RCI — come un appartamento Sunny Vacation Club a Caribbean World Mahdia." },
      { title: "Deposita la settimana", text: "Il membro deposita le proprie settimane vacanza nel sistema RCI, rendendole disponibili per lo scambio." },
      { title: "Riceve il Trading Power", text: "RCI assegna un valore chiamato Trading Power in base a stagione, qualità del resort, dimensione dell'unità, domanda e timing del deposito." },
      { title: "Scambia per un'altra vacanza", text: "I membri usano il loro Trading Power per prenotare vacanze in altri resort nel mondo." },
    ],
    additionalBenefits: "Vantaggi aggiuntivi",
    moreThan: "Più di un semplice scambio",
    benefits: [
      { title: "Scambi vacanze", items: ["Scambia la tua settimana per un'altra destinazione nel mondo", "Accedi a oltre 3.600 resort globalmente", "Quota di scambio forfettaria: €275 / settimana"] },
      { title: "Vacanze extra", items: ["Prenota vacanze aggiuntive in contanti", "Senza usare la settimana di proprietà", "Ideale per arricchire il tuo calendario viaggi"] },
      { title: "Sconti hotel", items: ["Usa il Trading Power per sconti su oltre 600.000 hotel nel mondo", "Copre grandi catene e hotel boutique", "Disponibile direttamente tramite il portale RCI"] },
    ],
    forVacationClubs: "Per i vacation club",
    whyValuable: "Perché RCI è prezioso",
    whyText: "RCI è ideale per progetti come vacation club, resort residence e proprietà in multiproprietà perché consente ai proprietari di viaggiare liberamente nel mondo invece di restare in un'unica location.",
    whyPoints: [
      "Offre flessibilità di viaggio internazionale ai membri",
      "Aggiunge valore alla proprietà resort",
      "Aumenta l'attrattività dei progetti vacation club",
      "Connette i resort a una rete di viaggio globale",
    ],
    ctaTitle: "Interessato a Sunny Vacation Club?",
    ctaText: "I membri di Caribbean World Mahdia ricevono una membership RCI di 3 anni inclusa nel loro package di proprietà.",
    ctaButton: "Contatta il team vendite",
    disclaimer: "Informazioni RCI fornite solo a scopo informativo. Le opportunità di scambio dipendono da eleggibilità, disponibilità, regole del resort, stagione e costi applicabili.",
  },
  de: {
    backToSite: "← Zurück zur Website",
    partnerNetwork: "Partnernetzwerk",
    whatIsRCI: "Was ist RCI?",
    heroText: "RCI ist das weltweit führende Ferienaustausch-Netzwerk für Timeshare- und Ferienimmobilienbesitzer. Gegründet 1974, ermöglicht es Mitgliedern, ihre Ferienwochen zu tauschen und tausende Resorts weltweit zu bereisen.",
    visitRCI: "RCI.com besuchen →",
    affiliatedResorts: "Angeschlossene Resorts",
    countries: "Länder",
    memberFamilies: "Mitgliedsfamilien",
    bookingsWorldwide: "Buchungen weltweit",
    howItWorks: "So funktioniert es",
    fourSteps: "4 Schritte zum Ferientausch",
    steps: [
      { title: "Das Mitglied besitzt eine Ferienwoche", text: "Ein Mitglied besitzt eine Woche oder ein Apartment in einem RCI-angeschlossenen Resort — wie ein Sunny Vacation Club Apartment in Caribbean World Mahdia." },
      { title: "Die Woche einzahlen", text: "Das Mitglied zahlt seine Ferienwochen in das RCI-System ein und macht sie so für einen Tausch verfügbar." },
      { title: "Trading Power erhalten", text: "RCI weist einen Wert namens Trading Power zu, basierend auf Saison, Resortqualität, Einheitsgröße, Nachfrage und Einzahlungszeitpunkt." },
      { title: "Gegen einen anderen Urlaub tauschen", text: "Mitglieder nutzen ihre Trading Power, um Urlaube in anderen Resorts weltweit zu buchen." },
    ],
    additionalBenefits: "Weitere Vorteile",
    moreThan: "Mehr als nur ein Tausch",
    benefits: [
      { title: "Ferientausch", items: ["Tauschen Sie Ihre Woche gegen ein anderes Ziel weltweit", "Zugang zu über 3.600 Resorts global", "Pauschale Tauschgebühr: 275 € / Woche"] },
      { title: "Extra-Urlaube", items: ["Buchen Sie zusätzliche Urlaube gegen Geld", "Ohne Ihre Eigentümerwoche zu nutzen", "Ideal zur Ergänzung Ihres Reisekalenders"] },
      { title: "Hotelrabatte", items: ["Nutzen Sie Trading Power für Rabatte bei über 600.000 Hotels weltweit", "Umfasst große Ketten und Boutique-Hotels", "Direkt über das RCI-Portal verfügbar"] },
    ],
    forVacationClubs: "Für Vacation Clubs",
    whyValuable: "Warum RCI wertvoll ist",
    whyText: "RCI ist ideal für Projekte wie Vacation Clubs, Resort-Residenzen und Timeshare-ähnliche Immobilien, da Eigentümer weltweit flexibel reisen können, statt nur an einem Ort zu bleiben.",
    whyPoints: [
      "Bietet Mitgliedern internationale Reiseflexibilität",
      "Steigert den Wert von Resort-Eigentum",
      "Erhöht die Attraktivität von Vacation-Club-Projekten",
      "Verbindet Resorts mit einem globalen Reisennetzwerk",
    ],
    ctaTitle: "Interesse an Sunny Vacation Club?",
    ctaText: "Caribbean World Mahdia Mitglieder erhalten eine 3-jährige RCI-Mitgliedschaft inklusive ihres Eigentumspakets.",
    ctaButton: "Vertrieb kontaktieren",
    disclaimer: "RCI-Informationen nur zur Orientierung. Tauschmöglichkeiten unterliegen der Mitgliedsberechtigung, Verfügbarkeit, Resortregeln, Saison und anfallenden Gebühren.",
  },
  ar: {
    backToSite: "→ العودة إلى الموقع",
    partnerNetwork: "شبكة الشركاء",
    whatIsRCI: "ما هي RCI؟",
    heroText: "RCI هي الشبكة العالمية الرائدة لتبادل العطلات لأصحاب العقارات السياحية. تأسست عام 1974، وتتيح للأعضاء تبادل أسابيع عطلهم والسفر إلى آلاف المنتجعات حول العالم.",
    visitRCI: "زيارة RCI.com ←",
    affiliatedResorts: "منتجع شريك",
    countries: "دولة",
    memberFamilies: "عائلة عضو",
    bookingsWorldwide: "حجز حول العالم",
    howItWorks: "كيف تعمل",
    fourSteps: "4 خطوات لتبادل عطلتك",
    steps: [
      { title: "العضو يمتلك أسبوع عطلة", text: "يمتلك العضو أسبوعاً أو شقة في منتجع مرتبط بـ RCI — مثل شقة نادي ساني في كاريبيان وورلد المهدية." },
      { title: "إيداع الأسبوع", text: "يودع العضو أسابيع عطلاته في نظام RCI، لتصبح متاحة للتبادل." },
      { title: "استلام Trading Power", text: "تمنح RCI قيمة تُسمى Trading Power بناءً على الموسم، وجودة المنتجع، وحجم الوحدة، والطلب، وتوقيت الإيداع." },
      { title: "التبادل لعطلة أخرى", text: "يستخدم الأعضاء Trading Power الخاص بهم لحجز عطل في منتجعات أخرى حول العالم." },
    ],
    additionalBenefits: "مزايا إضافية",
    moreThan: "أكثر من مجرد تبادل",
    benefits: [
      { title: "تبادل العطلات", items: ["بادل أسبوعك بوجهة أخرى في أي مكان بالعالم", "الوصول إلى أكثر من 3,600 منتجع عالمياً", "رسوم تبادل ثابتة: 275 يورو / أسبوع"] },
      { title: "عطل إضافية", items: ["احجز عطلاً إضافية بالنقد", "دون الحاجة لاستخدام أسبوعك المملوك", "مثالي لتعزيز تقويم سفرك"] },
      { title: "خصومات الفنادق", items: ["استخدم Trading Power للحصول على خصومات في أكثر من 600,000 فندق", "يشمل السلاسل الكبرى والفنادق الفاخرة", "متاح مباشرة عبر بوابة RCI"] },
    ],
    forVacationClubs: "لنوادي العطلات",
    whyValuable: "لماذا RCI ذات قيمة",
    whyText: "RCI مثالية للمشاريع كنوادي العطلات والإقامات السياحية والعقارات المشتركة لأنها تتيح للمالكين السفر بمرونة حول العالم بدلاً من البقاء في موقع واحد.",
    whyPoints: [
      "تمنح الأعضاء مرونة السفر الدولي",
      "تضيف قيمة لملكية المنتجع",
      "تزيد جاذبية مشاريع نوادي العطلات",
      "تربط المنتجعات بشبكة سفر عالمية",
    ],
    ctaTitle: "مهتم بنادي ساني للعطلات؟",
    ctaText: "يحصل أعضاء كاريبيان وورلد المهدية على عضوية RCI لمدة 3 سنوات ضمن باقة ملكيتهم.",
    ctaButton: "تواصل مع فريق المبيعات",
    disclaimer: "معلومات RCI مقدمة للمرجعية فقط. تخضع فرص التبادل لأهلية العضوية والتوافر وقواعد المنتجع والموسم والرسوم المطبقة.",
  },
}

translations.es = {
  "backToSite": "← Volver al sitio",
  "partnerNetwork": "Red de socios",
  "whatIsRCI": "¿Qué es RCI?",
  "heroText": "RCI es la red de intercambio de vacaciones líder en el mundo para propietarios de propiedades vacacionales y de tiempo compartido. Fundada en 1974, permite a los miembros intercambiar sus semanas de vacaciones y viajar a miles de complejos turísticos en todo el mundo.",
  "visitRCI": "Visita RCI.com →",
  "affiliatedResorts": "Resorts afiliados",
  "countries": "Países",
  "memberFamilies": "Familias miembros",
  "bookingsWorldwide": "Reservas en todo el mundo",
  "howItWorks": "Cómo funciona",
  "fourSteps": "4 pasos para intercambiar tus vacaciones",
  "steps": [
    {
      "title": "El miembro posee una semana de vacaciones",
      "text": "Un miembro es propietario de una semana o un apartamento en un resort afiliado a RCI, como un apartamento Sunny Vacation Club en Caribbean World Mahdia."
    },
    {
      "title": "Deposita la semana",
      "text": "El miembro deposita sus semanas de vacaciones en el sistema RCI, poniéndolas a disposición para su intercambio."
    },
    {
      "title": "Reciba poder de intercambio",
      "text": "RCI asigna un valor denominado poder de intercambio según la temporada, la calidad del complejo, el tamaño de la unidad, la demanda y el momento del depósito."
    },
    {
      "title": "Intercambio por otras vacaciones",
      "text": "Los miembros utilizan su poder de intercambio para reservar vacaciones en otros complejos turísticos de todo el mundo."
    }
  ],
  "additionalBenefits": "Beneficios adicionales",
  "moreThan": "Más que un simple intercambio",
  "benefits": [
    {
      "title": "Vacaciones de intercambio",
      "items": [
        "Cambia tu semana por otro destino en el mundo",
        "Acceda a más de 3600 complejos turísticos en todo el mundo",
        "Tarifa de cambio fijo de 275 € por semana"
      ]
    },
    {
      "title": "Vacaciones adicionales",
      "items": [
        "Reserve vacaciones adicionales con efectivo",
        "No es necesario utilizar tu propia semana",
        "Ideal para recargar tu calendario de viajes"
      ]
    },
    {
      "title": "Descuentos en hoteles",
      "items": [
        "Utilice Trading Power para obtener descuentos en más de 600.000 hoteles en todo el mundo",
        "Cubre las principales cadenas y propiedades boutique.",
        "Disponible directamente a través del portal RCI"
      ]
    }
  ],
  "forVacationClubs": "Para clubes de vacaciones",
  "whyValuable": "Por qué RCI es valioso",
  "whyText": "RCI es ideal para proyectos como clubes vacacionales, residencias turísticas y propiedades de tiempo compartido porque permite a los propietarios disfrutar de alojamiento flexible en todo el mundo en lugar de quedarse en un solo lugar.",
  "whyPoints": [
    "Brinda flexibilidad a los viajes internacionales a los miembros",
    "Agrega valor a la propiedad del resort",
    "Aumenta el atractivo de los proyectos de clubes vacacionales.",
    "Conecta complejos turísticos a una red de viajes global"
  ],
  "ctaTitle": "¿Interesado en Sunny Vacation Club?",
  "ctaText": "Los miembros de Caribbean World Mahdia reciben una membresía RCI de 3 años incluida con su paquete de propiedad.",
  "ctaButton": "Póngase en contacto con el equipo de ventas",
  "disclaimer": "La información de RCI se proporciona únicamente como referencia. Las oportunidades de intercambio están sujetas a la elegibilidad de la membresía, la disponibilidad, las reglas del resort, la temporada y las tarifas aplicables."
};

export default function RCIPage() {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("sunny_language") || "en";
  });

  useEffect(() => {
    localStorage.setItem("sunny_language", language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
  }, [language]);

  const t = translations[language] || translations.en;
  const isRTL = language === "ar";

  const stats = [
    { value: "3,600+", label: t.affiliatedResorts },
    { value: "100+",   label: t.countries },
    { value: "3.3M",   label: t.memberFamilies },
    { value: "50M+",   label: t.bookingsWorldwide },
  ];

  return (
    <main className="min-h-screen bg-[#FCFBF8] font-sans" dir={isRTL ? "rtl" : "ltr"} lang={language}>

      {/* Language bar — always visible at the very top */}
      <div className="border-b border-[#E5E7EB] bg-white px-5 py-3">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4">
          <p className="text-xs font-bold uppercase tracking-widest text-[#6B7280]" aria-label="Select language">&#127760; Language</p>
          <div className="flex gap-2">
            {languageOptions.map((opt) => (
              <button
                key={opt.code}
                type="button"
                onClick={() => setLanguage(opt.code)}
                className={`inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-sm font-black uppercase transition ${
                  language === opt.code
                    ? "bg-[#0B1C2C] text-[#E5C07B]"
                    : "bg-[#F0EDE8] text-[#0B1C2C] hover:bg-[#E5E7EB]"
                }`}
              >
                <FlagMark code={opt.code} /> {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Header */}
      <header className={`${luxuryGradient} px-5 py-6 text-white`}>
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-3">
            <img src="/brand/sunny-logo-header.png" alt="Sunny Vacation Club" loading="lazy" decoding="async" className="h-10 w-20 rounded-xl bg-white object-contain p-1 ring-1 ring-[#E5C07B]/70" />
            <span className="text-sm font-black uppercase tracking-widest text-[#E5C07B]">Sunny Vacation Club</span>
          </a>
          <a href="/" className="rounded-full border border-white/20 px-4 py-2 text-sm font-black text-white hover:bg-white/10">{t.backToSite}</a>
        </div>
      </header>

      {/* Hero */}
      <section className={`${luxuryGradient} px-5 pb-16 pt-12 text-white`}>
        <div className="mx-auto max-w-4xl text-center">
          <span className={`${goldGradient} inline-block rounded-full px-4 py-1 text-xs font-black uppercase tracking-widest text-[#0B1C2C]`}>{t.partnerNetwork}</span>
          <h1 className="mt-5 text-4xl font-black leading-tight tracking-tight md:text-6xl">
            <span className="bg-gradient-to-r from-[#E5C07B] via-white to-[#C9A96A] bg-clip-text text-transparent">{t.whatIsRCI}</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-200">{t.heroText}</p>
          <a href="https://www.rci.com" target="_blank" rel="noopener noreferrer" className={`${goldGradient} mt-8 inline-block rounded-full px-7 py-3 font-black text-[#0B1C2C] shadow-lg hover:brightness-110`}>{t.visitRCI}</a>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white px-5 py-14">
        <div className="mx-auto grid max-w-4xl grid-cols-2 gap-5 md:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className={`${luxuryGradient} rounded-[1.5rem] p-6 text-center text-white shadow-lg`}>
              <p className="text-3xl font-black text-[#E5C07B]">{s.value}</p>
              <p className="mt-2 text-sm font-semibold text-slate-200">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#FCFBF8] px-5 py-16">
        <div className="mx-auto max-w-4xl">
          <p className="text-sm font-black uppercase tracking-[0.3em] text-[#8A6A25]">{t.howItWorks}</p>
          <h2 className="mt-3 text-3xl font-black text-[#0B1C2C] md:text-4xl">{t.fourSteps}</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {t.steps.map((step, i) => (
              <div key={i} className="rounded-[1.5rem] border border-[#E5E7EB] bg-white p-6 shadow-sm">
                <div className={`${luxuryGradient} mb-4 flex h-11 w-11 items-center justify-center rounded-2xl text-lg font-black text-[#E5C07B]`}>{i + 1}</div>
                <h3 className="text-lg font-black text-[#0B1C2C]">{step.title}</h3>
                <p className="mt-2 text-sm leading-7 text-[#4B5563]">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-white px-5 py-16">
        <div className="mx-auto max-w-4xl">
          <p className="text-sm font-black uppercase tracking-[0.3em] text-[#8A6A25]">{t.additionalBenefits}</p>
          <h2 className="mt-3 text-3xl font-black text-[#0B1C2C] md:text-4xl">{t.moreThan}</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {t.benefits.map((b) => (
              <div key={b.title} className={`${luxuryGradient} rounded-[1.5rem] p-6 text-white`}>
                <h3 className="text-lg font-black text-[#E5C07B]">{b.title}</h3>
                <ul className="mt-4 space-y-2">
                  {b.items.map((item) => (
                    <li key={item} className="flex gap-2 text-sm leading-6 text-slate-200">
                      <span className="mt-0.5 shrink-0 text-[#E5C07B]">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why RCI */}
      <section className="bg-[#FCFBF8] px-5 py-16">
        <div className="mx-auto max-w-4xl">
          <p className="text-sm font-black uppercase tracking-[0.3em] text-[#8A6A25]">{t.forVacationClubs}</p>
          <h2 className="mt-3 text-3xl font-black text-[#0B1C2C] md:text-4xl">{t.whyValuable}</h2>
          <p className="mt-4 text-lg leading-8 text-[#4B5563]">{t.whyText}</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {t.whyPoints.map((point) => (
              <div key={point} className="flex gap-3 rounded-[1.2rem] border border-[#E5E7EB] bg-white p-5 text-sm font-semibold text-[#0B1C2C] shadow-sm">
                <span className="shrink-0 text-[#8A6A25]">✓</span>
                {point}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`${luxuryGradient} px-5 py-14 text-white`}>
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-2xl font-black md:text-3xl">{t.ctaTitle}</h2>
          <p className="mt-3 text-slate-200">{t.ctaText}</p>
          <a href="/#contact" onClick={() => saveLead({ source: "rci_page_cta", channel: "link", language })} className={`${goldGradient} mt-7 inline-block rounded-full px-8 py-4 font-black text-[#0B1C2C] shadow-lg hover:brightness-110`}>{t.ctaButton}</a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0B1C2C] px-5 py-8 text-center text-sm text-slate-400">
        <p>© Sunny Vacation Club — <a href="/" className="text-[#E5C07B] hover:underline">sunnyvacationclub.com</a></p>
        <div className="mt-3 flex flex-wrap justify-center gap-4 text-xs font-bold">
          <a href="/privacy" className="text-[#E5C07B] hover:underline">Privacy Policy</a>
          <a href="/terms" className="text-[#E5C07B] hover:underline">Terms & Conditions</a>
          <a href="/cancellation-refund" className="text-[#E5C07B] hover:underline">Cancellation & Refund Policy</a>
          <a href="/#contact" className="text-[#E5C07B] hover:underline">Contact Us</a>
        </div>
        <p className="mt-2 text-xs">{t.disclaimer}</p>
      </footer>
    </main>
  );
}
